import { Link } from 'react-router';
import type { IdentityKeyEntry, ObjectMetadata } from '../../data/client';
import { useRecordDuplicates, useRecordIdentity } from '../../data/hooks';
import { GroupedForm, GroupedFormRow, Skeleton } from '../../ui';
import { DuplicateRow } from './DuplicateRow';

/**
 * WHAT THIS RECORD IS KNOWN BY, in the Overview's own construction. Three things, in the
 * order somebody asks them:
 *
 *  1. WHAT THIS RECORD IS KNOWN BY - its keys, current and historical. The alias set
 *     is why a feeder holding last year's handle still finds this person (1.5), and a
 *     historical value is marked as such because it is a weaker claim: numbers are
 *     recycled, handles are reassigned.
 *  2. POSSIBLE DUPLICATES of this record, each with the guard that held it back.
 *  3. THE SAME IDENTIFIER ELSEWHERE - the cross-object read (2.6). The key index is
 *     global, so "who else holds this number" is one indexed query. It answers the
 *     question before a call and it can never cause a merge, which is why it is
 *     allowed to cross an object boundary when merging is not.
 *
 * WAVE 3 (R-B1): THREE GROUP BOXES, NOT ONE CARD WITH THREE CAPITALISED SUB-HEADINGS.
 * A bordered `Card` holding sections whose titles were letter-spaced capitals was the last
 * wave-1 component rendering inside the rebuilt record, and it is the thing owner-24
 * ("Irgendwie passt das farblich nicht") and owner-25 were looking at. System Settings draws
 * a details list as sibling group boxes on the pane with a sentence-case title above each and
 * no frame around the set, which is what `spec/screens/record.md` section 2 item 6 asks the
 * Overview to be made of - so these three become three of them and inherit the Overview's
 * own row, hairline, inset and ground for free.
 * kit: Apple macOS 27 UI Kit.sketch / Group Boxes/{Light,Dark}, Forms/Example Forms/Light
 */
export interface RecordIdentityCardProps {
  readonly object: ObjectMetadata;
  readonly recordId: string;
}

export function RecordIdentityCard({ object, recordId }: RecordIdentityCardProps) {
  const panel = useRecordIdentity(object.id, recordId);
  const duplicates = useRecordDuplicates(object.id, recordId);

  /**
   * ONE ROW PER VALUE, not one per key row.
   *
   * After a merge the winner legitimately owns TWO key rows for the value that made the
   * pair match - one whose origin is the winner, one whose origin is the loser - because
   * `meta.identity_key` anchors uniqueness on `origin_record_id` so the repoint cannot
   * collide (identity-resolution.md 5.1). That is correct storage and the wrong thing to
   * put on a screen: "Email address team@lunamedia.de" listed twice reads as a bug in
   * the data, on the panel whose whole job is to tell somebody what this record is known
   * by. Matching itself reads `select distinct record_id` for the same reason.
   *
   * Keyed on namespace + value + current, so an address the record HELD and then took
   * back is still one row, and a value that is current stays distinguishable from the
   * same value marked "Previously".
   */
  const keys = dedupeByValue(panel.data?.keys ?? []);
  const elsewhere = panel.data?.elsewhere ?? [];
  const pairs = duplicates.data ?? [];

  // Nothing on this object identifies a person and nothing points here: the boxes
  // would be empty frames explaining a feature this object does not use.
  if (
    !panel.isPending &&
    !duplicates.isPending &&
    keys.length === 0 &&
    elsewhere.length === 0 &&
    pairs.length === 0
  ) {
    return null;
  }

  if (panel.isPending) {
    return (
      <GroupedForm title="Known by">
        <div className="record-row record-row-stacked" aria-busy="true">
          <Skeleton className="h-4 w-48" label="Loading the identity panel" />
          <Skeleton className="h-4 w-56" />
        </div>
      </GroupedForm>
    );
  }

  return (
    <>
      {keys.length > 0 && (
        <GroupedForm title="Known by">
          {keys.map((key) => (
            <GroupedFormRow
              key={`${key.namespaceId}-${key.valueNorm}`}
              label={key.namespaceLabel}
              /* "Previously" is a NOTE under the value, not a capsule beside it: the value
                 is the row's content and the note is what qualifies it, which is the shape
                 the kit's Forms row gives a hint. */
              {...(key.isCurrent
                ? {}
                : { note: <span className="text-2xs text-ink-muted">Previously</span> })}
            >
              {key.valueNorm}
            </GroupedFormRow>
          ))}
        </GroupedForm>
      )}

      {pairs.length > 0 && (
        <GroupedForm
          title="Possible duplicates"
          footer={`Records in ${object.labelPlural.toLowerCase()} only. Nothing is merged until somebody says so.`}
          actions={
            <Link
              to={`/objects/${object.id}/duplicates`}
              className="text-2xs text-accent"
              /* The pane's own way to the full queue. The accent role marks it as a link;
                 the underline the wave-1 card carried is a web convention and the wave-3
                 behaviour rule takes it off rows. */
            >
              See all
            </Link>
          }
        >
          <ul>
            {pairs.map((duplicate) => (
              <DuplicateRow
                key={`${duplicate.recordId}-${duplicate.otherRecordId}`}
                object={object}
                duplicate={duplicate}
                fromRecordId={recordId}
              />
            ))}
          </ul>
        </GroupedForm>
      )}

      {elsewhere.length > 0 && (
        <GroupedForm
          title="Same identifier elsewhere"
          /* The one thing a reader could get wrong about this box, said once under it in the
             kit's own help line rather than in a boxed note with an info glyph inside the
             list. It is a statement of fact, not a warning. */
          footer="A record in another object is never merged into this one. This is a lookup."
        >
          {elsewhere.map((hit) => (
            <GroupedFormRow
              key={`${hit.objectId}-${hit.recordId}-${hit.valueNorm}`}
              label={hit.namespaceLabel}
              stacked
            >
              <p className="text-sm text-ink">
                <Link
                  to={`/objects/${hit.objectId}/records/${hit.recordId}`}
                  className="font-medium text-accent"
                >
                  {hit.label}
                </Link>
                <span className="text-ink-muted">
                  {' '}
                  holds the same {hit.namespaceLabel.toLowerCase()}, {hit.valueNorm}
                </span>
              </p>
            </GroupedFormRow>
          ))}
        </GroupedForm>
      )}
    </>
  );
}

/** 5.1: several key ROWS can carry one value; a reader wants the value. */
function dedupeByValue(keys: readonly IdentityKeyEntry[]): readonly IdentityKeyEntry[] {
  const seen = new Map<string, IdentityKeyEntry>();
  for (const key of keys) {
    const id = `${key.namespaceId}|${key.valueNorm}|${String(key.isCurrent)}`;
    if (!seen.has(id)) seen.set(id, key);
  }
  return [...seen.values()];
}
